import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Tag, Plus, Trash2, X } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/utils'
import { useAuth } from '../context/AuthContext'
import CsvControls from '../components/CsvControls'

interface DiscountCode {
  id: string
  code: string
  type: string
  value: number
  user_id: string | null
  user_name: string | null
  user_phone: string | null
  game_play_id: string | null
  valid_until: string | null
  used_at: string | null
  order_id: string | null
  max_uses: number | null
  times_used: number
  created_at: string
}

function formatDiscount(code: DiscountCode) {
  if (code.type === 'percentage') return `${code.value}% off`
  if (code.type === 'fixed') return `JD ${(code.value / 1000).toFixed(3)} off`
  return 'Free delivery'
}

function CreateCodeDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [codeInput, setCodeInput] = useState('')
  const [type, setType] = useState('percentage')
  const [value, setValue] = useState('10')
  const [validUntil, setValidUntil] = useState('')
  const [maxUses, setMaxUses] = useState('')
  const [error, setError] = useState('')

  const create = useMutation({
    mutationFn: (body: object) => api.post('/discounts', body).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['discount-codes'] })
      onClose()
    },
    onError: (e: any) => setError(e.response?.data?.message ?? 'Failed to create code'),
  })

  const handleSubmit = (ev: React.FormEvent) => {
    ev.preventDefault()
    create.mutate({
      code: codeInput.trim().toUpperCase() || undefined,
      type,
      value: parseInt(value || '0', 10),
      valid_until: validUntil || undefined,
      max_uses: maxUses ? parseInt(maxUses, 10) : undefined,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Tag size={18} className="text-[#c41e3a]" />
            <h2 className="text-white font-semibold">Create Discount Code</h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Code <span className="text-gray-600">(leave blank to auto-generate)</span></label>
            <input
              type="text"
              value={codeInput}
              onChange={e => setCodeInput(e.target.value.toUpperCase())}
              placeholder="e.g. SUMMER20"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono tracking-wide placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#c41e3a]"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Type</label>
              <select
                value={type}
                onChange={e => setType(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#c41e3a]"
              >
                <option value="percentage">% Off</option>
                <option value="fixed">Fixed Amount (fils)</option>
                <option value="free_delivery">Free Delivery</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                Value {type === 'percentage' ? '(%)' : type === 'fixed' ? '(fils)' : '(ignored)'}
              </label>
              <input
                type="number"
                min="0"
                max={type === 'percentage' ? 100 : undefined}
                value={value}
                onChange={e => setValue(e.target.value)}
                disabled={type === 'free_delivery'}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#c41e3a] disabled:opacity-40"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Expires <span className="text-gray-600">(optional)</span></label>
              <input
                type="date"
                value={validUntil}
                onChange={e => setValidUntil(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#c41e3a]"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Max uses <span className="text-gray-600">(optional)</span></label>
              <input
                type="number"
                min="1"
                value={maxUses}
                onChange={e => setMaxUses(e.target.value)}
                placeholder="Unlimited"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#c41e3a]"
              />
            </div>
          </div>

          {error && <p className="text-red-400 text-sm">{error}</p>}

          <button
            type="submit"
            disabled={create.isPending}
            className="w-full px-4 py-2.5 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830] disabled:opacity-50"
          >
            {create.isPending ? 'Creating…' : 'Create Code'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default function DiscountCodes() {
  const { user } = useAuth()
  const canCreate = user?.role === 'super_admin' || user?.role === 'operations'
  const canDelete = user?.role === 'super_admin'
  const qc = useQueryClient()

  const [showCreate, setShowCreate] = useState(false)
  const [sourceFilter, setSourceFilter] = useState<'all' | 'admin' | 'game'>('all')

  const { data, isLoading } = useQuery<{ codes: DiscountCode[] }>({
    queryKey: ['discount-codes', sourceFilter],
    queryFn: () => {
      const params = sourceFilter !== 'all' ? `?source=${sourceFilter}` : ''
      return api.get(`/discounts${params}`).then(r => r.data)
    },
  })

  const deleteCode = useMutation({
    mutationFn: (id: string) => api.delete(`/discounts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['discount-codes'] }),
  })

  const codes = data?.codes ?? []

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-lg font-semibold text-white">Discount Codes</h1>
        {canCreate && (
          <div className="flex items-center gap-2">
            <CsvControls
              basePath="/discount-codes"
              templateFilename="discount-codes-template.csv"
              exportFilename={`discount-codes-export-${new Date().toISOString().slice(0, 10)}.csv`}
              label="discount code"
              onImported={() => qc.invalidateQueries({ queryKey: ['discount-codes'] })}
            />
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830]"
            >
              <Plus size={15} />
              New Code
            </button>
          </div>
        )}
      </div>

      <div className="flex gap-2 mb-5">
        {(['all', 'admin', 'game'] as const).map(f => (
          <button
            key={f}
            onClick={() => setSourceFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              sourceFilter === f
                ? 'bg-[#c41e3a]/20 text-[#c41e3a]'
                : 'bg-white/5 text-gray-400 hover:text-white'
            }`}
          >
            {f === 'all' ? 'All' : f === 'admin' ? 'Admin-Created' : 'Game Prizes'}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-gray-500 text-sm">Loading…</div>
      ) : codes.length === 0 ? (
        <div className="text-center py-16 text-gray-600">
          <Tag size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No discount codes yet</p>
        </div>
      ) : (
        <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-600 border-b border-[#2e2e2e]">
                <th className="px-4 py-3 text-left font-medium">Code</th>
                <th className="px-4 py-3 text-left font-medium">Discount</th>
                <th className="px-4 py-3 text-left font-medium">Customer</th>
                <th className="px-4 py-3 text-left font-medium">Uses</th>
                <th className="px-4 py-3 text-left font-medium">Expires</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-left font-medium">Created</th>
                {canDelete && <th className="px-4 py-3 w-10" />}
              </tr>
            </thead>
            <tbody>
              {codes.map(c => (
                <tr key={c.id} className="border-b border-[#222]">
                  <td className="px-4 py-3">
                    <span className="font-mono text-white text-xs tracking-wide bg-white/5 px-2 py-1 rounded">{c.code}</span>
                    {c.game_play_id && (
                      <span className="ml-2 text-[10px] text-purple-400 bg-purple-400/10 px-1.5 py-0.5 rounded">Game</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-300">{formatDiscount(c)}</td>
                  <td className="px-4 py-3">
                    {c.user_name ? (
                      <>
                        <p className="text-white text-xs">{c.user_name}</p>
                        <p className="text-gray-500 text-xs">{c.user_phone}</p>
                      </>
                    ) : (
                      <span className="text-gray-600 text-xs">Public</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {c.times_used}{c.max_uses ? `/${c.max_uses}` : ''}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                    {c.valid_until ? formatDateTime(c.valid_until) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {c.used_at ? (
                      <span className="inline-flex px-2 py-0.5 rounded-full bg-gray-500/15 text-gray-400 text-xs">Used</span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 text-xs">Active</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{formatDateTime(c.created_at)}</td>
                  {canDelete && (
                    <td className="px-4 py-3">
                      {!c.used_at && (
                        <button
                          onClick={() => deleteCode.mutate(c.id)}
                          disabled={deleteCode.isPending}
                          className="text-gray-600 hover:text-red-400 transition-colors disabled:opacity-40"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && <CreateCodeDialog onClose={() => setShowCreate(false)} />}
    </div>
  )
}
